import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type FormEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import {
  motion,
  useScroll,
  useTransform,
  type MotionValue,
} from 'framer-motion';
import { ArrowRight, Check, ChevronUp, Globe, Menu, Plus, X } from 'lucide-react';
import { zh } from './content/zh';

const t = zh;

const ASSET = '/landing-assets';
const SPOTLIGHT_R = 260;

// app 子域现网只认 .html 路径（/dashboard /login /privacy /terms 均 404，2026-10-01 实测）
const APP_ORIGIN = 'https://app.accrobrief.com';
const APP_URL = `${APP_ORIGIN}/dashboard.html`;
const LOGIN_URL = `${APP_ORIGIN}/auth.html`;
const PRIVACY_URL = `${APP_ORIGIN}/privacy.html`;
const TERMS_URL = `${APP_ORIGIN}/terms.html`;

// 占位内容未定稿前不上线：创始人姓名/头像、博客文章都还是草稿
const SHOW_FOUNDER = false;
const SHOW_BLOG = false;

// 作品墙暂用现有 showreel 素材（无音轨、非自家成片），所以不配标题、不开带声音观看
const REEL = [
  '01-space', '02-codenest', '04-stellar-v2', '06-transform',
  '08-terra', '10-aethera', '14-orbit', '17-planet',
];
const HERO_VIDEO = '01-space';

const EASE = [0.16, 1, 0.3, 1] as const;

function explainerUrl(topic: string) {
  return `${APP_URL}?mode=explainer&topic=${encodeURIComponent(topic)}`;
}

function Arrow({ size = 14 }: { size?: number }) {
  return <ArrowRight size={size} strokeWidth={1.8} aria-hidden="true" />;
}

function LogoMark({ size = 36, iconSize = 22 }: { size?: number; iconSize?: number }) {
  return (
    <span className="grid shrink-0 place-items-center rounded-full bg-white" style={{ width: size, height: size }}>
      <img src="/logo.svg" alt="" style={{ width: iconSize, height: iconSize }} />
    </span>
  );
}

function FadeIn({ children, delay = 0, className = '' }: { children: ReactNode; delay?: number; className?: string }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 36, filter: 'blur(10px)' }}
      whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
      viewport={{ once: true, margin: '50px', amount: 0 }}
      transition={{ duration: 0.8, delay, ease: EASE }}
    >
      {children}
    </motion.div>
  );
}

// 视频只在进入视口（外扩 100px）时播放，离开即暂停
function useInViewPlayback() {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const video = ref.current;
    if (!video) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) video.play().catch(() => {});
      else video.pause();
    }, { rootMargin: '100px' });
    observer.observe(video);
    return () => observer.disconnect();
  }, []);
  return ref;
}

function Nav() {
  const [open, setOpen] = useState(false);
  const links = [
    [t.nav.stories, '#stories'],
    [t.nav.how, '#how'],
    ...(SHOW_BLOG ? [[t.nav.blog, '#blog']] : []),
    [t.nav.faq, '#faq'],
  ];

  return (
    <nav className="fixed inset-x-0 top-0 z-[120] px-4 pt-4">
      <div className="mx-auto flex h-14 max-w-[1480px] items-center gap-6 rounded-full border border-white/15 bg-black/40 pl-2.5 pr-2 backdrop-blur-[20px]">
        <a href="#top" aria-label={t.nav.home}><LogoMark /></a>

        <div className="hidden flex-1 justify-center gap-7 text-sm text-white/[.78] md:flex">
          {links.map(([label, href]) => (
            <a key={href} href={href} className="py-1.5 transition-colors duration-200 hover:text-white">{label}</a>
          ))}
        </div>

        <div className="hidden items-center md:flex">
          <a href={LOGIN_URL} className="px-3 py-2 text-sm text-white/80 transition-colors hover:text-white">{t.nav.login}</a>
          <a href={APP_URL} className="btn-gradient">{t.nav.start}<Arrow /></a>
        </div>

        <div className="flex-1 md:hidden" />
        <button
          type="button"
          className="grid size-10 place-items-center rounded-full bg-white text-ink md:hidden"
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
          aria-label={open ? t.nav.closeMenu : t.nav.openMenu}
        >
          {open ? <X size={16} /> : <Menu size={16} />}
        </button>
      </div>

      {open && (
        <div className="animate-pop mx-1 mt-2 flex flex-col gap-1 rounded-3xl border border-white/15 bg-[rgba(20,20,20,.96)] p-3 md:hidden">
          {links.map(([label, href]) => (
            <a key={href} href={href} onClick={() => setOpen(false)} className="rounded-2xl px-4 py-3 text-sm text-white/[.85] hover:bg-white/10">{label}</a>
          ))}
          <a href={LOGIN_URL} className="rounded-2xl px-4 py-3 text-sm text-white/[.85] hover:bg-white/10">{t.nav.login}</a>
          <a href={APP_URL} className="btn-gradient mt-1 !h-11 justify-center">{t.nav.start}</a>
        </div>
      )}
    </nav>
  );
}

function Hero() {
  const [topic, setTopic] = useState('');
  const videoRef = useInViewPlayback();

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const value = topic.trim();
    window.location.href = value ? explainerUrl(value) : APP_URL;
  };

  return (
    <header id="top" className="relative flex flex-col items-center overflow-hidden bg-ink px-5 pt-[clamp(128px,18vh,176px)]">
      <div className="hero-glow pointer-events-none absolute inset-0" aria-hidden="true" />

      <h1 className="text-gradient animate-rise relative z-10 m-0 text-center text-[clamp(52px,8.4vw,144px)] font-black leading-[.95] tracking-[-.05em] [animation-delay:.1s] [animation-duration:1.1s]">
        {t.hero.titleLines.map((line) => <span key={line} className="block">{line}</span>)}
      </h1>

      <div className="animate-rise relative z-10 mt-8 flex w-full flex-col items-center gap-3.5 [animation-delay:.35s]">
        <p className="m-0 text-pretty text-center text-base text-white/[.82]">{t.hero.lead}</p>
        <form
          onSubmit={submit}
          className="flex h-[60px] w-full max-w-[640px] items-center gap-2 rounded-full border border-white/20 bg-black/[.55] pl-[22px] pr-1.5 shadow-[0_20px_60px_rgba(0,0,0,.4)] backdrop-blur-[20px]"
        >
          <input
            value={topic}
            onChange={(event) => setTopic(event.target.value)}
            aria-label={t.hero.inputLabel}
            placeholder={t.hero.placeholder}
            className="min-w-0 flex-1 border-0 bg-transparent text-base text-white outline-none placeholder:text-white/55"
          />
          <button type="submit" className="btn-white h-12 px-5">
            <span className="hidden sm:inline">{t.hero.submit}</span>
            <span className="sm:hidden">{t.hero.submitShort}</span>
            <Arrow />
          </button>
        </form>
        <div className="flex flex-wrap justify-center gap-2">
          {t.hero.chips.map((chip) => (
            <button
              key={chip}
              type="button"
              onClick={() => setTopic(chip)}
              className="h-8 rounded-full border border-white/[.18] bg-white/[.06] px-3.5 text-[13px] text-white/[.85] transition-colors duration-200 hover:border-white/30 hover:bg-white/[.12]"
            >
              {chip}
            </button>
          ))}
        </div>
        <span className="text-[13px] text-white/65">{t.hero.altStart}</span>
      </div>

      <div className="animate-rise relative z-10 mt-16 w-full max-w-[1120px] [animation-delay:.6s] [animation-duration:1s]">
        <div className="pointer-events-none absolute -inset-px rounded-t-[29px] bg-[linear-gradient(180deg,rgba(139,124,248,.55),rgba(139,124,248,0)_70%)]" />
        <div className="relative aspect-video overflow-hidden rounded-t-[28px] bg-[#141416] shadow-[0_-20px_80px_rgba(99,84,245,.25)]">
          <video
            ref={videoRef}
            src={`${ASSET}/showreel/${HERO_VIDEO}.mp4`}
            muted
            loop
            playsInline
            preload="auto"
            aria-label={t.hero.videoLabel}
            className="block size-full object-cover"
          />
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[35%] bg-[linear-gradient(180deg,transparent,#0C0C0C)]" />
        </div>
      </div>
    </header>
  );
}

function ReelTile({ file }: { file: string }) {
  const videoRef = useInViewPlayback();
  return (
    <div className="relative aspect-[14/9] w-[clamp(280px,30vw,420px)] shrink-0 overflow-hidden rounded-3xl border border-white/10 bg-[#171717] transition-[transform,box-shadow] duration-300 ease-[cubic-bezier(.2,.8,.2,1)] hover:scale-[1.03] hover:shadow-[0_24px_60px_rgba(0,0,0,.55)]">
      <video
        ref={videoRef}
        src={`${ASSET}/showreel/${file}.mp4`}
        muted
        loop
        playsInline
        preload="metadata"
        aria-label={t.stories.tileLabel}
        className="block size-full object-cover"
      />
    </div>
  );
}

function ReelRow({ files, direction }: { files: string[]; direction: 'right' | 'left' }) {
  const { scrollY } = useScroll();
  const x = useTransform(scrollY, (value) => {
    const distance = value * 0.16;
    return direction === 'right' ? distance - 460 : 150 - distance;
  });
  return (
    <motion.div className="flex w-max gap-3 py-2 will-change-transform" style={{ x }}>
      {[...files, ...files].map((file, index) => <ReelTile key={`${file}-${index}`} file={file} />)}
    </motion.div>
  );
}

function Stories() {
  return (
    <section id="stories" aria-label={t.stories.eyebrow} className="scroll-mt-24 overflow-hidden bg-ink pb-20 pt-28">
      <div className="mx-auto mb-10 flex max-w-[1400px] flex-wrap items-end justify-between gap-4 px-6">
        <div className="flex flex-col gap-3">
          <span className="eyebrow">{t.stories.eyebrow}</span>
          <h2 className="m-0 text-[clamp(36px,4.6vw,64px)] font-black leading-none tracking-[-.05em] text-[#EDEFF1]">{t.stories.title}</h2>
        </div>
        <p className="m-0 max-w-[340px] text-[15px] leading-relaxed text-white/[.72]">{t.stories.lead}</p>
      </div>
      <div className="flex flex-col gap-3">
        <ReelRow files={REEL.slice(0, 4)} direction="right" />
        <ReelRow files={REEL.slice(4)} direction="left" />
      </div>
    </section>
  );
}

function AnimatedCharacter({ character, index, total, progress }: {
  character: string;
  index: number;
  total: number;
  progress: MotionValue<number>;
}) {
  const start = index / total;
  const end = Math.min(1, start + 0.14);
  const opacity = useTransform(progress, [start, end], [0.2, 1]);
  return <motion.span style={{ opacity }}>{character}</motion.span>;
}

function About() {
  const sectionRef = useRef<HTMLElement>(null);
  // 与设计稿同一口径：进度 = (0.86·视口高 − 区块顶) / (0.7·区块高)
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ['start 0.86', 'end 0.86'] });
  const progress = useTransform(scrollYProgress, (value) => Math.min(1, value / 0.7));
  const characters = Array.from(t.about.body);
  const decor = [
    ['moon', 'left-[2%] top-[6%] w-[clamp(110px,14vw,200px)]', '0s'],
    ['object', 'bottom-[7%] left-[7%] w-[clamp(90px,12vw,170px)]', '-2.8s'],
    ['lego', 'right-[2%] top-[6%] w-[clamp(110px,14vw,200px)]', '-1.4s'],
    ['group', 'bottom-[7%] right-[6%] w-[clamp(110px,14vw,205px)]', '-4s'],
  ];

  return (
    <section id="about" ref={sectionRef} className="relative flex min-h-[100dvh] items-center justify-center overflow-hidden px-6 py-28">
      {decor.map(([name, position, delay]) => (
        <img key={name} src={`${ASSET}/decor/${name}.png`} alt="" className={`about-decor ${position}`} style={{ animationDelay: delay }} />
      ))}
      <div className="relative z-[5] flex max-w-[1100px] flex-col items-center text-center">
        <FadeIn>
          <h2 className="text-gradient m-0 text-[clamp(52px,8vw,120px)] font-black leading-none tracking-[-.06em]">
            {t.about.titleLines.map((line) => <span key={line} className="block">{line}</span>)}
          </h2>
        </FadeIn>
        <p className="mb-0 mt-[72px] max-w-[760px] text-[clamp(20px,2.2vw,32px)] font-medium leading-[1.75] tracking-[-.02em] text-[#E6E8EE]">
          {characters.map((character, index) => (
            <AnimatedCharacter key={index} character={character} index={index} total={characters.length} progress={progress} />
          ))}
        </p>
        <FadeIn delay={0.1}>
          <img
            src={`${ASSET}/hero-presenter.png`}
            alt={t.about.presenterAlt}
            className="pointer-events-none mt-14 h-[clamp(300px,42vh,460px)] w-auto [filter:drop-shadow(0_30px_70px_rgba(99,84,245,.28))_drop-shadow(0_30px_60px_rgba(0,0,0,.6))]"
          />
        </FadeIn>
      </div>
    </section>
  );
}

function revealMask(x: number, y: number) {
  return `radial-gradient(circle ${SPOTLIGHT_R}px at ${x}px ${y}px, #fff 0%, #fff 40%, rgba(255,255,255,.75) 60%, rgba(255,255,255,.4) 75%, rgba(255,255,255,.12) 88%, transparent 100%)`;
}

function RevealLayer({ x, y }: { x: number; y: number }) {
  const mask = revealMask(x, y);
  const style: CSSProperties = {
    backgroundImage: `url(${ASSET}/growth-reveal.webp)`,
    WebkitMaskImage: mask,
    maskImage: mask,
  };
  return <div className="growth-image pointer-events-none absolute inset-0" style={style} aria-hidden="true" />;
}

function Growth() {
  const ref = useRef<HTMLElement>(null);
  const target = useRef({ x: -999, y: -999 });
  const smooth = useRef({ x: -999, y: -999 });
  const raf = useRef(0);
  const [cursor, setCursor] = useState<{ x: number; y: number } | null>(null);
  const [patches, setPatches] = useState<Array<{ x: number; y: number }>>([]);

  useEffect(() => () => cancelAnimationFrame(raf.current), []);

  // 平滑跟随（系数 .12），只在指针位于区块内时跑 rAF
  const loop = () => {
    const s = smooth.current;
    s.x += (target.current.x - s.x) * 0.12;
    s.y += (target.current.y - s.y) * 0.12;
    setCursor({ x: Math.round(s.x), y: Math.round(s.y) });
    raf.current = requestAnimationFrame(loop);
  };

  const position = (event: ReactPointerEvent<HTMLElement>) => {
    const rect = ref.current!.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };

  const enter = (event: ReactPointerEvent<HTMLElement>) => {
    const point = position(event);
    target.current = point;
    smooth.current = { ...point };
    cancelAnimationFrame(raf.current);
    raf.current = requestAnimationFrame(loop);
  };

  const leave = () => {
    cancelAnimationFrame(raf.current);
    setCursor(null);
  };

  const down = (event: ReactPointerEvent<HTMLElement>) => {
    if ((event.target as HTMLElement).closest('a, button')) return;
    const point = position(event);
    setPatches((current) => [...current.slice(-8), point]);
  };

  return (
    <section
      id="growth"
      ref={ref}
      className="relative h-[100dvh] min-h-[680px] cursor-crosshair overflow-hidden bg-black"
      onPointerEnter={enter}
      onPointerMove={(event) => { target.current = position(event); }}
      onPointerLeave={leave}
      onPointerDown={down}
    >
      <div className="growth-image absolute inset-0" style={{ backgroundImage: `url(${ASSET}/growth-base.webp)` }} />
      {patches.map((patch, index) => <RevealLayer key={index} x={patch.x} y={patch.y} />)}
      {cursor && <RevealLayer x={cursor.x} y={cursor.y} />}
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,.42)_0%,transparent_33%,transparent_62%,rgba(0,0,0,.68)_100%)]" />

      <div className="pointer-events-none absolute inset-x-0 top-[13%] flex flex-col items-center px-5 text-center">
        <FadeIn>
          <h2 className="m-0 font-normal leading-[.95] text-white">
            <span className="block font-serif text-[clamp(48px,7.5vw,112px)] italic tracking-[-.05em]">{t.growth.titleSerif}</span>
            <span className="block text-[clamp(48px,7.5vw,112px)] tracking-[-.07em]">{t.growth.titleSans}</span>
          </h2>
          <p className="mb-0 mt-4 text-base text-white/[.85]">{t.growth.subtitle}</p>
        </FadeIn>
      </div>

      <p className="pointer-events-none absolute bottom-12 left-[clamp(20px,4vw,56px)] m-0 hidden max-w-[270px] text-sm leading-[1.65] text-white/[.82] sm:block">{t.growth.left}</p>

      <div className="absolute bottom-24 left-5 right-5 flex max-w-[290px] flex-col items-start gap-[18px] sm:bottom-12 sm:left-auto sm:right-[clamp(20px,4vw,56px)]">
        <p className="m-0 text-sm leading-[1.65] text-white/[.82]">{t.growth.right}</p>
        <a href="#how" className="btn-outline h-11 bg-black/25 px-5 backdrop-blur-[8px]">{t.growth.cta}<Arrow /></a>
      </div>

      <div className="absolute bottom-8 left-1/2 flex h-[34px] -translate-x-1/2 items-center gap-2 whitespace-nowrap rounded-full border border-white/[.22] bg-black/35 px-4 text-xs tracking-[.12em] text-white/80 backdrop-blur-[8px]">
        <span className="hidden md:inline">{t.growth.hint}</span>
        <span className="md:hidden">{t.growth.touchHint}</span>
        {patches.length > 0 && (
          <button type="button" onClick={() => setPatches([])} className="h-6 rounded-full bg-white/[.14] px-2.5 text-[11px] tracking-normal text-white">{t.growth.clear}</button>
        )}
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section id="how" className="scroll-mt-0 rounded-t-[48px] bg-white px-6 pb-44 pt-32 text-ink">
      <div className="mx-auto max-w-[1280px]">
        <FadeIn>
          <div className="mb-20 flex flex-wrap items-end justify-between gap-4">
            <h2 className="m-0 text-[clamp(64px,10vw,144px)] font-black leading-[.85] tracking-[-.07em]">{t.how.title}</h2>
            <p className="m-0 max-w-[320px] text-[15px] leading-relaxed text-black/65">{t.how.lead}</p>
          </div>
        </FadeIn>
        <div className="border-t border-black/15">
          {t.how.steps.map(([number, title, description], index) => (
            <FadeIn key={number} delay={index * 0.06}>
              <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,260px),1fr))] items-center gap-x-10 gap-y-4 border-b border-black/15 py-10">
                <span className="text-[clamp(56px,6vw,96px)] font-black leading-none tracking-[-.08em]">{number}</span>
                <h3 className="m-0 text-[clamp(24px,2.6vw,36px)] font-medium tracking-[-.04em]">{title}</h3>
                <p className="m-0 text-base leading-[1.65] text-black/65">{description}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}

function Founder() {
  return (
    <section className="px-6 pb-28">
      <FadeIn className="mx-auto flex max-w-[720px] flex-col gap-7">
        <span className="eyebrow">{t.founder.eyebrow}</span>
        <p className="m-0 text-pretty font-serif text-[clamp(22px,2.4vw,30px)] leading-[1.7] text-[#EDEFF1]">{t.founder.quote}</p>
        <div className="flex items-center gap-3.5">
          <span className="size-12 shrink-0 rounded-full bg-[repeating-linear-gradient(135deg,#2A2C31_0_6px,#33363C_6px_12px)]" />
          <div className="flex flex-col gap-0.5">
            <span className="text-base font-medium">{t.founder.name}</span>
            <span className="text-sm text-white/65">{t.founder.role}</span>
          </div>
        </div>
      </FadeIn>
    </section>
  );
}

function Blog() {
  return (
    <section id="blog" className="scroll-mt-24 px-6 pb-32">
      <div className="mx-auto flex max-w-[1280px] flex-col gap-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="flex flex-col gap-3">
            <span className="eyebrow">{t.blog.eyebrow}</span>
            <h2 className="m-0 max-w-[720px] text-pretty text-[clamp(32px,3.6vw,52px)] font-bold leading-[1.15] tracking-[-.04em]">{t.blog.title}</h2>
          </div>
          <a href="#blog" className="btn-outline h-10 px-4">{t.blog.all}<Arrow size={13} /></a>
        </div>
        <div className="grid grid-cols-[repeat(auto-fill,minmax(min(100%,340px),1fr))] gap-6">
          {t.blog.posts.map((post) => (
            <a key={post.title} href="#blog" className="flex flex-col gap-[18px] rounded-[28px] border border-white/10 bg-[#121212] px-3 pb-6 pt-3 transition-[transform,border-color,background] duration-[250ms] ease-[cubic-bezier(.2,.8,.2,1)] hover:-translate-y-1 hover:border-white/[.22] hover:bg-[#161616]">
              <div className="flex aspect-video items-end rounded-[20px] p-[18px]" style={{ background: post.cover }}>
                <span className="text-xs tracking-[.14em] text-white/70">{post.kind}</span>
              </div>
              <div className="flex flex-col gap-2.5 px-2">
                <span className="text-[13px] text-white/60">{post.date}</span>
                <h3 className="m-0 text-pretty text-[21px] font-medium leading-[1.35] tracking-[-.02em]">{post.title}</h3>
                <p className="m-0 text-pretty text-[15px] leading-[1.65] text-white/70">{post.summary}</p>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

function Faq() {
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="scroll-mt-24 px-6 pb-32">
      <div className="mx-auto flex max-w-[880px] flex-col gap-8">
        <h2 className="m-0 text-[clamp(36px,4vw,56px)] font-bold tracking-[-.04em]">{t.faq.title}</h2>
        <div className="border-t border-white/[.14]">
          {t.faq.items.map(([question, answer], index) => {
            const isOpen = open === index;
            return (
              <div key={question} className="border-b border-white/[.14]">
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? -1 : index)}
                  aria-expanded={isOpen}
                  className="flex min-h-[72px] w-full items-center gap-4 py-4 text-left text-[19px] font-medium text-white"
                >
                  <span className="flex-1">{question}</span>
                  <span className={`grid size-8 shrink-0 place-items-center rounded-full border border-white/25 transition-transform duration-[250ms] ${isOpen ? 'rotate-45' : ''}`}>
                    <Plus size={12} strokeWidth={1.8} />
                  </span>
                </button>
                {isOpen && <p className="animate-pop m-0 pb-6 pr-12 text-base leading-[1.7] text-white/75">{answer}</p>}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function LanguageSwitch() {
  const [open, setOpen] = useState(false);
  const languages = [
    { code: 'zh', label: '简体中文', ready: true },
    { code: 'en', label: 'English', ready: false },
  ];
  return (
    <div className="relative">
      {open && (
        <>
          <div className="fixed inset-0 z-[60]" onClick={() => setOpen(false)} />
          <div role="listbox" className="animate-pop absolute bottom-12 right-0 z-[61] flex w-[200px] flex-col gap-0.5 rounded-[20px] border border-white/[.14] bg-[#161616] p-1.5 shadow-[0_18px_44px_rgba(0,0,0,.5)]">
            {languages.map((language) => {
              const selected = language.code === 'zh';
              return (
                <button
                  key={language.code}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  aria-disabled={!language.ready}
                  onClick={() => { if (language.ready) setOpen(false); }}
                  className={`flex h-10 items-center gap-2 rounded-[14px] px-3 text-left text-sm transition-colors hover:bg-white/[.06] ${selected ? 'bg-white/[.08]' : ''} ${language.ready ? 'cursor-pointer text-white' : 'cursor-default text-white/50'}`}
                >
                  <span className="flex-1">{language.label}</span>
                  {!language.ready && <span className="text-xs text-white/50">{t.footer.comingSoon}</span>}
                  {selected && <Check size={14} strokeWidth={1.7} />}
                </button>
              );
            })}
          </div>
        </>
      )}
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t.footer.languageLabel}
        className="flex h-9 items-center gap-2 rounded-full border border-white/[.18] pl-2.5 pr-3 text-[13px] text-white/[.85] transition-colors hover:border-white/40"
      >
        <Globe size={15} strokeWidth={1.4} />
        简体中文
        <ChevronUp size={10} strokeWidth={1.8} />
      </button>
    </div>
  );
}

function Footer() {
  const productLinks = [
    [t.nav.stories, '#stories'],
    [t.nav.how, '#how'],
    [t.nav.faq, '#faq'],
    ...(SHOW_BLOG ? [[t.nav.blog, '#blog']] : []),
    [t.footer.workspace, APP_URL],
  ];
  return (
    <footer className="px-5 pb-8">
      <div className="mx-auto flex max-w-[1420px] flex-wrap items-end justify-between gap-8 rounded-[48px] bg-indigo-soft px-[clamp(24px,5vw,56px)] py-16 text-ink">
        <div className="flex flex-col gap-5">
          <span className="text-[13px] tracking-[.24em] text-indigo-text">{t.footer.ctaEyebrow}</span>
          <h2 className="m-0 text-[clamp(56px,8vw,116px)] font-black leading-[.9] tracking-[-.06em]">
            {t.footer.ctaTitleLines.map((line) => <span key={line} className="block">{line}</span>)}
          </h2>
        </div>
        <a href={APP_URL} className="flex h-14 items-center gap-2.5 rounded-full bg-ink px-6 text-[15px] font-semibold text-white transition-transform duration-200 hover:-translate-y-0.5 active:scale-[.97]">{t.footer.cta}<Arrow /></a>
      </div>

      <div className="mx-auto mt-[72px] flex max-w-[1420px] flex-wrap gap-x-16 gap-y-12 border-t border-white/[.12] pt-12">
        <div className="flex flex-[1_1_280px] flex-col gap-4">
          <a href="#top" aria-label={t.footer.backToTop} className="w-fit"><LogoMark size={40} iconSize={24} /></a>
          <p className="m-0 max-w-[360px] text-sm leading-relaxed text-white/65">{t.footer.tagline}</p>
        </div>
        <div className="flex min-w-[140px] flex-col gap-3.5">
          <span className="text-[13px] font-semibold text-white/[.85]">{t.footer.product}</span>
          {productLinks.map(([label, href]) => <a key={label} href={href} className="footer-link">{label}</a>)}
        </div>
        <div className="flex min-w-[140px] flex-col gap-3.5">
          <span className="text-[13px] font-semibold text-white/[.85]">{t.footer.legal}</span>
          <a href={PRIVACY_URL} className="footer-link">{t.footer.privacy}</a>
          <a href={TERMS_URL} className="footer-link">{t.footer.terms}</a>
        </div>
      </div>

      <div className="mx-auto mt-14 flex max-w-[1420px] flex-wrap items-center justify-between gap-4 border-t border-white/10 pt-6 text-[13px] text-white/55">
        <span>{t.footer.copyright}</span>
        <LanguageSwitch />
      </div>
    </footer>
  );
}

export default function App() {
  return (
    <main className="overflow-x-clip bg-ink font-sans text-paper">
      <Nav />
      <Hero />
      <Stories />
      <About />
      <Growth />
      <HowItWorks />
      {/* 深色区块压在白色区块上（圆角 48 重叠 48px） */}
      <div className="relative -mt-12 rounded-t-[48px] bg-ink pt-32">
        {SHOW_FOUNDER && <Founder />}
        {SHOW_BLOG && <Blog />}
        <Faq />
      </div>
      <Footer />
    </main>
  );
}
